import {Component, OnInit, ViewChild, OnDestroy} from '@angular/core';
import {MatTableDataSource} from '@angular/material/table';
import {MatSort} from '@angular/material/sort';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {RagService, RagQA} from '../../services/ai/rag.service';
import {RagAddDialogComponent} from './rag-add-dialog.component';
import {RagEditDialogComponent} from './rag-edit-dialog.component';
import {UpdateTutorialsDialogComponent} from './update-tutorials-dialog.component';
import {GptResponseDialogComponent} from './gpt-response-dialog.component';
import {TutorialsComponent} from '../../components/tutorials/tutorials.component';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';

interface TreeNode {
    text: string;
    videoid?: string;
    videoId?: string;
    children?: TreeNode[];
    state?: any;
    icon?: string;
    lesson?: boolean;
    url?: string;
    [key: string]: any;
}

interface TutorialItem {
    name: string;
    path: string;
    videoId: string;
}

@Component({
    selector: 'app-rag',
    templateUrl: './rag.component.html',
    styleUrls: ['./rag.component.scss']
})
export class RagComponent implements OnInit, OnDestroy {

    @ViewChild(MatSort) sort: MatSort;

    displayedColumns: string[] = ['id', 'question', 'answer', 'category', 'videoId', 'createdDate'];
    searchColumns: string[] = ['id', 'question', 'answer', 'category', 'videoId', 'similarity', 'createdDate'];
    dataSource: MatTableDataSource<RagQA>;
    private destroy$ = new Subject<void>();
    isLoading = false;
    isUpdating = false;
    selectedItem: RagQA | null = null;
    searchQuery: string = '';
    isSearchMode: boolean = false;
    searchLimit: number = 10;
    limitOptions: number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
    searchThreshold: number = 0.7; // Default threshold value
    thresholdOptions = [
        { label: '70%', value: 0.7 },
        { label: '75%', value: 0.75 },
        { label: '80%', value: 0.8 },
        { label: '85%', value: 0.85 },
        { label: '90%', value: 0.9 },
        { label: '95%', value: 0.95 }
    ];
    private lastTutorialJson: string = ''; // Store the last JSON content

    get currentColumns(): string[] {
        return this.isSearchMode ? this.searchColumns : this.displayedColumns;
    }

    constructor(
        private ragService: RagService,
        private dialog: MatDialog,
        private snackBar: MatSnackBar
    ) {}

    ngOnInit(): void {
        this.loadData();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    loadData(): void {
        this.isLoading = true;
        this.ragService.getAllQA()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (data) => {
                    this.dataSource = new MatTableDataSource(data);
                    if (this.sort) {
                        this.dataSource.sort = this.sort;
                    }
                    this.isLoading = false;
                },
                error: (error) => {
                    console.error('Error loading Q&A data:', error);
                    this.isLoading = false;
                }
            });
    }

    ngAfterViewInit() {
        if (this.dataSource) {
            this.dataSource.sort = this.sort;
        }
    }

    openAddDialog(): void {
        const dialogRef = this.dialog.open(RagAddDialogComponent, {
            width: '600px',
            data: {}
        });

        dialogRef.afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe(result => {
                if (result) {
                    this.addQA(result);
                }
            });
    }

    addQA(qa: Partial<RagQA>): void {
        this.isLoading = true;
        this.ragService.addQA(qa)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (newQA) => {
                    this.snackBar.open('Q&A added successfully', 'Close', {
                        duration: 3000
                    });
                    this.loadData();
                },
                error: (error) => {
                    console.error('Error adding Q&A:', error);
                    this.snackBar.open('Error adding Q&A. Please try again.', 'Close', {
                        duration: 5000
                    });
                    this.isLoading = false;
                }
            });
    }

    openEditDialog(qa: RagQA): void {
        const dialogRef = this.dialog.open(RagEditDialogComponent, {
            width: '600px',
            data: qa
        });

        dialogRef.afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe(result => {
                if (result) {
                    this.updateQA(result);
                }
            });
    }

    updateQA(qa: RagQA): void {
        this.isLoading = true;
        this.ragService.updateQA(qa)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (updatedQA) => {
                    this.snackBar.open('Q&A updated successfully', 'Close', {
                        duration: 3000
                    });
                    this.loadData();
                },
                error: (error) => {
                    console.error('Error updating Q&A:', error);
                    this.snackBar.open('Error updating Q&A. Please try again.', 'Close', {
                        duration: 5000
                    });
                    this.isLoading = false;
                }
            });
    }

    deleteQA(id: number): void {
        const confirmDelete = confirm('Are you sure you want to delete this Q&A pair?');

        if (confirmDelete) {
            this.isLoading = true;
            this.ragService.deleteQA(id)
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: () => {
                        this.snackBar.open('Q&A deleted successfully', 'Close', {
                            duration: 3000
                        });
                        this.loadData();
                        this.selectedItem = null;
                    },
                    error: (error) => {
                        console.error('Error deleting Q&A:', error);
                        this.snackBar.open('Error deleting Q&A. Please try again.', 'Close', {
                            duration: 5000
                        });
                        this.isLoading = false;
                    }
                });
        }
    }

    selectRow(row: RagQA): void {
        this.selectedItem = this.selectedItem?.id === row.id ? null : row;
    }

    isRowSelected(row: RagQA): boolean {
        return this.selectedItem?.id === row.id;
    }

    editSelected(): void {
        if (this.selectedItem) {
            this.openEditDialog(this.selectedItem);
        }
    }

    deleteSelected(): void {
        if (this.selectedItem) {
            this.deleteQA(this.selectedItem.id);
        }
    }

    searchSimilar(): void {
        if (!this.searchQuery || this.searchQuery.trim() === '') {
            this.snackBar.open('Please enter a question to search', 'Close', {
                duration: 3000
            });
            return;
        }

        this.isLoading = true;
        this.isSearchMode = true;
        this.ragService.searchSimilar(this.searchQuery, this.searchLimit, this.searchThreshold)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (data) => {
                    this.dataSource = new MatTableDataSource(data);
                    if (this.sort) {
                        this.dataSource.sort = this.sort;
                    }
                    this.isLoading = false;
                    if (data.length === 0) {
                        this.snackBar.open('No similar questions found', 'Close', {
                            duration: 3000
                        });
                    } else {
                        this.snackBar.open(`Found ${data.length} similar Q&A${data.length > 1 ? 's' : ''}`, 'Close', {
                            duration: 3000
                        });
                    }
                },
                error: (error) => {
                    console.error('Error searching similar Q&A:', error);
                    this.snackBar.open('Error searching. Please try again.', 'Close', {
                        duration: 5000
                    });
                    this.isLoading = false;
                    this.isSearchMode = false;
                }
            });
    }

    clearSearch(): void {
        this.searchQuery = '';
        this.isSearchMode = false;
        this.loadData();
    }

    onVideoIdClick(videoId: string): void {
        if (videoId) {
            console.log('Video ID clicked:', videoId);

            // Open the TutorialsComponent dialog with the videoId
            const dialogRef = this.dialog.open(TutorialsComponent, {
                width: '100vw',
                maxWidth: '100vw',
                height: '100vh',
                panelClass: 'full-width-dialog',
                data: {
                    videoLink: videoId,  // Pass the videoId as videoLink
                    lessonMode: false    // Set to false by default, adjust if needed
                }
            });

            // Optionally handle dialog close
            dialogRef.afterClosed()
                .pipe(takeUntil(this.destroy$))
                .subscribe(result => {
                    console.log('Tutorial dialog closed', result);
                });
        }
    }

    /**
     * Convert hierarchical JSON tree to flat tutorial list
     */
    private convertJsonToTutorialList(jsonData: any): TutorialItem[] {
        const tutorials: TutorialItem[] = [];

        // Helper function to recursively process tree nodes
        const processNode = (node: TreeNode, parentPath: string = ''): void => {
            // Build current path
            const currentPath = parentPath ? `${parentPath} > ${node.text}` : node.text;

            // If node has videoid/videoId, add it to the list
            const videoId = node.videoid || node.videoId || '';
            if (videoId) {
                tutorials.push({
                    name: node.text,
                    path: currentPath,
                    videoId: videoId
                });
            }

            // Process children recursively
            if (node.children && Array.isArray(node.children)) {
                node.children.forEach(child => {
                    processNode(child, currentPath);
                });
            }
        };

        // Start processing from root
        if (jsonData) {
            if (Array.isArray(jsonData)) {
                jsonData.forEach(item => processNode(item));
            } else if (jsonData.children) {
                // If root has children, process them
                jsonData.children.forEach((child: TreeNode) => {
                    processNode(child, jsonData.text || '');
                });
            } else {
                // Single node
                processNode(jsonData);
            }
        }

        return tutorials;
    }

    updateTutorials(): void {
        const dialogRef = this.dialog.open(UpdateTutorialsDialogComponent, {
            width: '600px',
            data: {
                content: this.lastTutorialJson // Pass the preserved content
            }
        });

        dialogRef.afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe(result => {
                if (result && result.content) {
                    // Save the content for next time
                    this.lastTutorialJson = result.content;

                    try {
                        // Parse the JSON content on client side ONLY - no server calls
                        let jsonData: any;

                        try {
                            jsonData = JSON.parse(result.content);
                            console.log('Parsed JSON data:', jsonData);
                        } catch (parseError) {
                            console.error('Failed to parse JSON:', parseError);
                            this.snackBar.open(`Invalid JSON format: ${parseError.message}`, 'Close', {
                                duration: 5000
                            });
                            return;
                        }

                        // Convert JSON tree to flat tutorial list on client side
                        const tutorials = this.convertJsonToTutorialList(jsonData);
                        console.log('Converted tutorials:', tutorials);

                        if (!tutorials || tutorials.length === 0) {
                            this.snackBar.open('No tutorials found in the JSON data', 'Close', {
                                duration: 5000
                            });
                            return;
                        }

                        // Immediately open the dialog with client-processed data - NO API CALL
                        this.dialog.open(GptResponseDialogComponent, {
                            width: '900px',
                            maxHeight: '80vh',
                            data: {
                                tutorials: tutorials,
                                message: `Successfully processed ${tutorials.length} tutorials locally`,
                                count: tutorials.length
                            }
                        });

                        this.snackBar.open(`Processed ${tutorials.length} tutorials successfully`, 'Close', {
                            duration: 3000
                        });

                    } catch (error) {
                        console.error('Error processing tutorials - Full error:', error);
                        console.error('Error stack:', error.stack);
                        this.snackBar.open(`Error processing tutorials: ${error.message}`, 'Close', {
                            duration: 5000
                        });
                    }
                }
            });
    }

}
